import { Button } from '@bp/ui-components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { FC, Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PreviewSectionContext, previewMapSections } from '../../../utils/previewMapSections';
import { useTeachingBlockStore } from '../Contexts/TeachingBlockContext';
import { PreviewGrid, WeekColumn } from './PreviewGrid';
import styles from './PreviewMap.module.scss';
import { allowedRowUuids, buildPreviewRows, filterPreviewRows, pickedResourceUuids } from './previewMapRows';

const CONTEXTS: PreviewSectionContext[] = ['classes', 'teachers', 'rooms'];

/**
 * Kompakte Mini-Map des Epochenplans (Zeilen × Wochen). Sie kann unabhängig vom Board-Kontext
 * Klassen, Lehrkräfte oder Räume zeigen; belegte Wochen tragen ihre Kartenfarbe wie im Hauptboard.
 * Eine Karte in der Hand behält im Epochenplan ihre Steckung → sie wird exakt hervorgehoben, alle
 * übrigen belegten Zellen werden abgedunkelt (Fokus).
 *
 * Mit Karte in der Hand zeigt die Automatik mehrere Abschnitte untereinander (die im Board nicht
 * sichtbaren Ressourcen), je auf die für die Karte relevanten Zeilen eingeschränkt. Die
 * Nachbarstunden-Markierung des Stundenplans gibt es hier nicht – die Achse sind Wochen.
 */
export const PreviewMap: FC = observer(() => {
  const store = useTeachingBlockStore();
  const { t } = useTranslation();

  // Dynamisch zusammengesetzte i18n-Keys erkennt `update:translations` nicht und löscht sie →
  // die Abschnitts-Labels bewusst statisch auflisten.
  const contextLabels: Record<PreviewSectionContext, string> = {
    classes: t('pinboard.previewMap.context.classes'),
    teachers: t('pinboard.previewMap.context.teachers'),
    rooms: t('pinboard.previewMap.context.rooms'),
  };

  const pickedCard = store.pickedCard?.card;
  const auto = store.previewMapAuto && Boolean(pickedCard);
  // `previewMapContext` ist ein GridContext und kennt dadurch auch `subjects`; die Vorschau hat
  // dafür keine Zeilen und fällt auf Klassen zurück.
  const manualContext: PreviewSectionContext =
    store.previewMapContext === 'subjects' ? 'classes' : store.previewMapContext;

  const sections: PreviewSectionContext[] = useMemo(
    () => (auto ? previewMapSections(store.gridContext) : [manualContext]),
    [auto, store.gridContext, manualContext],
  );

  const rowsByContext = useMemo(() => {
    const source = {
      classes: Array.from(store.classes.values()),
      teachers: Array.from(store.teachers.values()),
      rooms: Array.from(store.rooms.values()),
    };
    return {
      classes: buildPreviewRows('classes', source),
      teachers: buildPreviewRows('teachers', source),
      rooms: buildPreviewRows('rooms', source),
    };
  }, [store.classes, store.teachers, store.rooms]);

  // Wochen-Spalten wie im Grid: je Woche die ISO-KW + ISO-Wochenjahr des ersten Tages (KW-53-fest).
  const weekColumns: WeekColumn[] = useMemo(() => {
    return Array.from(store.weeks.entries())
      .map(([, weekDays]) => {
        const firstDay = weekDays[0];
        if (!firstDay) return null;
        const week = firstDay.weekNumber;
        const year = dayjs(firstDay.dateString).isoWeekYear();
        // „Ferienwoche", wenn die Mehrheit der Tage Ferien sind → als Band überlagert, damit die
        // Epoche wie im Board über die Ferien hinweg gesplittet aussieht (Näherung auf Wochen-Ebene).
        const holidayDays = weekDays.filter((day) => day.holidayUuid).length;
        const holiday = weekDays.length > 0 && holidayDays * 2 >= weekDays.length;
        return { key: `${week}:${year}`, week, year, label: `${week}`, holiday };
      })
      .filter((entry): entry is WeekColumn => entry !== null);
  }, [store.weeks]);

  // `allPlacedCards` statt `placedCards`: bei einem Ferien-/Zeitkonflikt wird die Karte gesplittet
  // und der Eltern-Datensatz aus `placedCards` entfernt (lebt nur noch als VirtualCards). Ohne die
  // gesplitteten Eltern fehlten genau die Karten mit Konflikten in der Vorschau.
  const placedCards = store.allPlacedCards;

  const visibleSections = useMemo(
    () =>
      sections.map((section) => ({
        section,
        rows: filterPreviewRows(
          rowsByContext[section],
          allowedRowUuids({ section, boardContext: store.gridContext, auto, card: pickedCard, cards: placedCards }),
        ),
        pickedResources: pickedResourceUuids(section, pickedCard),
      })),
    [sections, rowsByContext, store.gridContext, auto, pickedCard, placedCards],
  );

  // Mehrere Abschnitte untereinander nur dann bündig, wenn die Namensspalte überall gleich breit
  // ist – der sonst übliche `auto`-Wert richtet sich je Abschnitt nach dem längsten Namen.
  const nameColumnWidth = visibleSections.length > 1 ? '90px' : 'auto';
  const focusActive = Boolean(pickedCard?.startDate);

  return (
    <div className={styles['preview-map']}>
      <div className={styles.header}>
        <div className={styles.title}>{t('pinboard.previewMap.title')}</div>
        <div className={styles.switch}>
          <Button
            hierarchy={store.previewMapAuto ? 'secondary' : 'tertiary'}
            height='s'
            onClick={() => store.setPreviewMapAuto(true)}
          >
            {t('pinboard.previewMap.auto')}
          </Button>
          {CONTEXTS.map((ctx) => (
            <Button
              key={ctx}
              hierarchy={!store.previewMapAuto && ctx === manualContext ? 'secondary' : 'tertiary'}
              height='s'
              onClick={() => store.setPreviewMapContext(ctx)}
            >
              {contextLabels[ctx]}
            </Button>
          ))}
        </div>
      </div>

      <div className={styles['scroll-area']}>
        {visibleSections.map(({ section, rows, pickedResources }, index) => (
          <Fragment key={section}>
            {visibleSections.length > 1 && <div className={styles['section-title']}>{contextLabels[section]}</div>}
            <PreviewGrid
              context={section}
              rows={rows}
              pickedResources={pickedResources}
              weekColumns={weekColumns}
              showWeekHeader={index === 0}
              cards={placedCards}
              pickedCardUuid={pickedCard?.uuid}
              focusActive={focusActive}
              colorBySubject={store.cardColorBySubject}
              nameColumnWidth={nameColumnWidth}
            />
          </Fragment>
        ))}
      </div>
    </div>
  );
});

PreviewMap.displayName = 'TeachingBlockPreviewMap';
